import WebAPI from '../../Base/WebAPI';
import config from '../../../config.json';
import { safeInt, safeString } from '../../Base/Utils';

/**
 * Get all auth tokens for the current subscriber
 */
export const listAuthTokens = async (authContext, cancelToken) => {
  try {
    const response = await WebAPI.get(
      `/AuthToken/ListAuthTokens`,
      {
        headers: { AccessToken: authContext.token },
        cancelToken: cancelToken
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching auth tokens:', error);
    throw error;
  }
};

/**
 * Get auth token by provider and mode
 * Returns the specific configuration for a provider in a specific mode
 */
export const getAuthTokenByProviderAndMode = async (provider, mode, authContext, cancelToken) => {
  try {
    const allTokens = await listAuthTokens(authContext, cancelToken);

    // Filter by provider name and mode
    const matchingToken = allTokens.find(
      token =>
        token.providerName?.toLowerCase() === provider?.toLowerCase() &&
        safeInt(token.mode) === safeInt(mode)
    );

    return matchingToken || null;
  } catch (error) {
    console.error('Error fetching auth token by provider and mode:', error);
    throw error;
  }
};

/**
 * Get auth token by ID
 */
export const getAuthTokenById = async (authTokenId, authContext, cancelToken) => {
  try {
    const response = await WebAPI.get(
      `/AuthToken/GetAuthToken/${authTokenId}`,
      {
        headers: { AccessToken: authContext.token },
        cancelToken: cancelToken
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching auth token by ID:', error);
    throw error;
  }
};

/**
 * Create or update auth token
 */
export const createOrUpdateAuthToken = async (data, authContext, cancelToken) => {
  try {
    const isUpdate = data.id && safeInt(data.id) > 0;
    const endpoint = isUpdate ? '/AuthToken/UpdateAuthToken' : '/AuthToken/CreateAuthToken';

    const request = {
      ...data,
      applicationId: config.APPLICATION_ID,
      subscriberId: authContext.currentUser.subscriberId
    };

    const response = await WebAPI.put(
      endpoint,
      request,
      {
        headers: { AccessToken: authContext.token },
        cancelToken: cancelToken
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error creating/updating auth token:', error);
    throw error;
  }
};

/**
 * Delete auth token (soft delete)
 */
export const deleteAuthToken = async (authTokenId, authContext, cancelToken) => {
  try {
    const response = await WebAPI.put(
      '/AuthToken/DeleteAuthToken',
      safeString(authTokenId),
      {
        headers: { AccessToken: authContext.token },
        cancelToken: cancelToken
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error deleting auth token:', error);
    throw error;
  }
};

/**
 * Get all configurations for a specific provider (all modes)
 */
export const getProviderConfigurations = async (provider, authContext, cancelToken) => {
  try {
    const allTokens = await listAuthTokens(authContext, cancelToken);

    // Filter by provider name
    const providerTokens = allTokens.filter(
      token => token.providerName?.toLowerCase() === provider?.toLowerCase()
    );

    return providerTokens;
  } catch (error) {
    console.error('Error fetching provider configurations:', error);
    throw error;
  }
};
